import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Calendar,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Edit2,
  Filter,
  CalendarDays,
  ListChecks,
  X,
  Clock,
  MapPin,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { usHolidaysAround, holidaysOn } from "../lib/usHolidays";

// ---------------- helpers ----------------

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59);
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function sameDay(a, b) {
  return a.toDateString() === b.toDateString();
}
function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
}
function isOverdue(iso) {
  if (!iso) return false;
  return new Date(iso).getTime() < Date.now();
}
function toLocalIso(dt) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}
function fromLocalInput(localStr) {
  if (!localStr) return null;
  // localStr is yyyy-MM-ddTHH:mm — interpret in user's locale, send as UTC ISO
  const d = new Date(localStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return toLocalIso(d);
}

const COLOR_PILL = {
  slate: "bg-slate-200 text-slate-900",
  indigo: "bg-indigo-200 text-indigo-900",
  amber: "bg-amber-200 text-amber-900",
  emerald: "bg-emerald-200 text-emerald-900",
  rose: "bg-rose-200 text-rose-900",
  sky: "bg-sky-200 text-sky-900",
  violet: "bg-violet-200 text-violet-900",
};
const PRIORITY_PILL = {
  high: "bg-rose-100 text-rose-800 border-rose-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  low: "bg-slate-100 text-slate-700 border-slate-200",
};

// ---------------- main page ----------------

export default function Schedule() {
  const [view, setView] = useState("calendar"); // calendar | agenda | todos
  const [cursor, setCursor] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [todoFilter, setTodoFilter] = useState("open"); // open | all | done | high
  const [editing, setEditing] = useState(null); // {kind:'appointment'|'todo', data?:obj}

  const monthStart = useMemo(() => startOfMonth(cursor), [cursor]);
  const monthEnd = useMemo(() => endOfMonth(cursor), [cursor]);

  const loadCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const from = monthStart.toISOString();
      const to = monthEnd.toISOString();
      const [aRes, tRes] = await Promise.all([
        api.get(`/appointments?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
        api.get(`/todos?status=all`),
      ]);
      setAppointments(aRes.data?.items || []);
      setTodos(tRes.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [monthStart, monthEnd]);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  // -------- Calendar grid --------
  const grid = useMemo(() => {
    const first = startOfMonth(cursor);
    const startWeekday = first.getDay(); // 0=Sun
    const totalDays = endOfMonth(cursor).getDate();
    const cells = [];
    // pad start
    for (let i = 0; i < startWeekday; i++) {
      cells.push({ pad: true, key: `pad-s-${i}` });
    }
    for (let d = 1; d <= totalDays; d++) {
      cells.push({ date: new Date(cursor.getFullYear(), cursor.getMonth(), d), key: `d-${d}` });
    }
    while (cells.length % 7 !== 0) cells.push({ pad: true, key: `pad-e-${cells.length}` });
    return cells;
  }, [cursor]);

  // US holidays for the visible year (with +/- 1 year buffer so the
  // first / last row of the calendar grid still resolves correctly).
  const holidayLookup = useMemo(() => usHolidaysAround(cursor.getFullYear()), [cursor]);

  // Holiday-visibility toggle — Outlook-style. Persisted in localStorage
  // so the preference travels across reloads but never crosses devices.
  const [showHolidays, setShowHolidays] = useState(() => {
    try {
      const v = window.localStorage.getItem("rvm_show_holidays");
      return v === null ? true : v === "1";
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem("rvm_show_holidays", showHolidays ? "1" : "0");
    } catch { /* storage disabled — preference will just stay session-only */ }
  }, [showHolidays]);

  const eventsByDay = useMemo(() => {
    const map = {};
    appointments.forEach((a) => {
      const d = new Date(a.start_at);
      const key = d.toDateString();
      (map[key] ||= []).push({ kind: "appointment", data: a });
    });
    todos.forEach((t) => {
      if (!t.due_at) return;
      const d = new Date(t.due_at);
      const key = d.toDateString();
      (map[key] ||= []).push({ kind: "todo", data: t });
    });
    return map;
  }, [appointments, todos]);

  // -------- agenda groups --------
  const agendaGroups = useMemo(() => {
    const all = [
      ...appointments.map((a) => ({ kind: "appointment", at: a.start_at, data: a })),
      ...todos.filter((t) => t.due_at).map((t) => ({ kind: "todo", at: t.due_at, data: t })),
    ];
    all.sort((a, b) => (a.at || "").localeCompare(b.at || ""));
    const groups = [];
    let curr = null;
    all.forEach((it) => {
      const key = new Date(it.at).toDateString();
      if (!curr || curr.key !== key) {
        curr = { key, label: fmtDate(it.at), items: [] };
        groups.push(curr);
      }
      curr.items.push(it);
    });
    return groups;
  }, [appointments, todos]);

  // -------- todo list view filters --------
  const todosFiltered = useMemo(() => {
    let out = todos;
    if (todoFilter === "open") out = todos.filter((t) => t.status === "open");
    else if (todoFilter === "done") out = todos.filter((t) => t.status === "done");
    else if (todoFilter === "high") out = todos.filter((t) => t.priority === "high" && t.status === "open");
    return out;
  }, [todos, todoFilter]);

  const todoGroups = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const inWeek = addDays(today, 7);
    const groups = { Overdue: [], Today: [], "This week": [], Later: [], "No due date": [], Done: [] };
    todosFiltered.forEach((t) => {
      if (t.status === "done") {
        groups.Done.push(t);
        return;
      }
      if (!t.due_at) {
        groups["No due date"].push(t);
        return;
      }
      const due = new Date(t.due_at);
      if (due < today) groups.Overdue.push(t);
      else if (sameDay(due, today)) groups.Today.push(t);
      else if (due < inWeek) groups["This week"].push(t);
      else groups.Later.push(t);
    });
    return groups;
  }, [todosFiltered]);

  const completeTodo = async (id) => {
    try {
      await api.post(`/todos/${id}/complete`);
      toast.success("Marked done");
      setTodos((prev) => prev.map((t) => (t.id === id ? { ...t, status: "done", completed_at: new Date().toISOString() } : t)));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };
  const reopenTodo = async (id) => {
    try {
      await api.post(`/todos/${id}/reopen`);
      setTodos((prev) => prev.map((t) => (t.id === id ? { ...t, status: "open", completed_at: null } : t)));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };
  const deleteAppt = async (id) => {
    if (!window.confirm("Delete this appointment?")) return;
    try {
      await api.delete(`/appointments/${id}`);
      setAppointments((prev) => prev.filter((a) => a.id !== id));
      toast.success("Appointment deleted");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };
  const deleteTodo = async (id) => {
    if (!window.confirm("Delete this todo?")) return;
    try {
      await api.delete(`/todos/${id}`);
      setTodos((prev) => prev.filter((t) => t.id !== id));
      toast.success("Todo deleted");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <DashboardShell>
      <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }} data-testid="schedule-page-title">
              Schedule
            </h1>
            <p className="text-slate-600 text-sm mt-1">Appointments, todos, and reminders for your portfolio.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex bg-slate-100 rounded-md p-0.5" data-testid="schedule-view-toggle">
              {[
                { id: "calendar", label: "Calendar", icon: CalendarDays },
                { id: "agenda", label: "Agenda", icon: ListChecks },
                { id: "todos", label: "Todos", icon: CheckSquare },
              ].map((v) => (
                <button
                  key={v.id}
                  data-testid={`schedule-view-${v.id}`}
                  onClick={() => setView(v.id)}
                  className={`px-3 py-1.5 text-sm rounded ${
                    view === v.id ? "bg-white text-slate-900 shadow-sm font-semibold" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <v.icon className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                  {v.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => setEditing({ kind: "appointment" })}
              data-testid="schedule-add-appointment-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800"
            >
              <Plus className="w-4 h-4" /> Appointment
            </button>
            <button
              onClick={() => setEditing({ kind: "todo" })}
              data-testid="schedule-add-todo-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
            >
              <Plus className="w-4 h-4" /> Todo
            </button>
          </div>
        </div>

        {view === "calendar" && (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="schedule-calendar">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <button
                onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
                className="p-2 rounded hover:bg-slate-100"
                data-testid="schedule-prev-month"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="font-semibold text-slate-900" data-testid="schedule-month-label">
                {cursor.toLocaleDateString([], { month: "long", year: "numeric" })}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowHolidays((v) => !v)}
                  data-testid="schedule-toggle-holidays"
                  aria-pressed={showHolidays}
                  title={showHolidays ? "Hide US holidays on the calendar" : "Show US holidays on the calendar"}
                  className={`text-xs font-semibold inline-flex items-center gap-1 px-2.5 py-1 rounded border transition-colors mr-1
                    ${showHolidays
                      ? "bg-red-50 text-red-800 border-red-200 hover:bg-red-100"
                      : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}
                >
                  <span aria-hidden="true">🇺🇸</span> Holidays
                </button>
                <button
                  onClick={() => setCursor(new Date())}
                  className="text-xs px-2 py-1 rounded text-slate-700 hover:bg-slate-100"
                  data-testid="schedule-today-btn"
                >
                  Today
                </button>
                <button
                  onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                  className="p-2 rounded hover:bg-slate-100"
                  data-testid="schedule-next-month"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-7 text-[10px] uppercase tracking-widest font-bold text-slate-500 border-b border-slate-100">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div key={d} className="px-2 py-2 text-center">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7" style={{ minHeight: 480 }}>
              {grid.map((cell) => {
                if (cell.pad) return <div key={cell.key} className="border-r border-b border-slate-100 bg-slate-50/40" />;
                const isToday = sameDay(cell.date, new Date());
                const events = eventsByDay[cell.date.toDateString()] || [];
                const holidays = showHolidays ? holidaysOn(cell.date, holidayLookup) : [];
                const hasFederal = holidays.some((h) => h.federal);
                // Highlight cells differently when a federal holiday lands on
                // them. Today still wins because it's the most-glanced cell.
                const cellBg = isToday
                  ? "bg-amber-50/50"
                  : hasFederal
                    ? "bg-red-50/40"
                    : "";
                return (
                  <div
                    key={cell.key}
                    className={`border-r border-b border-slate-100 p-1.5 min-h-[88px] ${cellBg}`}
                    data-testid={`schedule-cell-${cell.date.getDate()}`}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <div className={`text-[11px] font-bold ${isToday ? "text-amber-700" : "text-slate-700"}`}>
                        {cell.date.getDate()}
                      </div>
                      {hasFederal && (
                        <span
                          className="text-[10px] leading-none"
                          aria-hidden="true"
                          title={holidays.filter((h) => h.federal).map((h) => h.name).join(" • ")}
                        >
                          🇺🇸
                        </span>
                      )}
                    </div>
                    {holidays.length > 0 && (
                      <div className="mt-1 space-y-0.5" data-testid={`schedule-holidays-${cell.date.getDate()}`}>
                        {holidays.map((h) => (
                          <div
                            key={h.name}
                            title={h.name + (h.observed ? " — federal observed day" : "")}
                            className={`text-[10px] truncate px-1.5 py-0.5 rounded font-semibold border ${
                              h.federal
                                ? "bg-red-100 text-red-800 border-red-200"
                                : "bg-blue-50 text-blue-700 border-blue-200"
                            }`}
                          >
                            {h.name}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="mt-1 space-y-0.5">
                      {events.slice(0, 3).map((ev) => (
                        <button
                          key={`${ev.kind}-${ev.data.id}`}
                          onClick={() => setEditing({ kind: ev.kind, data: ev.data })}
                          className={`block w-full text-left text-[10px] truncate px-1.5 py-0.5 rounded ${
                            ev.kind === "appointment"
                              ? COLOR_PILL[ev.data.color] || COLOR_PILL.slate
                              : ev.data.priority === "high"
                                ? COLOR_PILL.rose
                                : COLOR_PILL.amber
                          } hover:opacity-80`}
                          title={ev.data.title}
                        >
                          {ev.kind === "appointment" ? fmtTime(ev.data.start_at) + " " : ""}
                          {ev.data.title}
                        </button>
                      ))}
                      {events.length > 3 && (
                        <div className="text-[10px] text-slate-500 px-1.5">+{events.length - 3} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {view === "agenda" && (
          <div className="bg-white border border-slate-200 rounded-lg" data-testid="schedule-agenda">
            {loading && <div className="p-6 text-sm text-slate-500">Loading…</div>}
            {!loading && agendaGroups.length === 0 && (
              <div className="p-10 text-center text-sm text-slate-500">No appointments or todos for {cursor.toLocaleDateString([], { month: "long", year: "numeric" })}.</div>
            )}
            {agendaGroups.map((g) => (
              <div key={g.key} className="border-b border-slate-100 last:border-b-0">
                <div className="px-5 py-2.5 bg-slate-50 text-xs uppercase tracking-widest text-slate-600 font-bold">{g.label}</div>
                {g.items.map((it) => (
                  <button
                    key={`${it.kind}-${it.data.id}`}
                    onClick={() => setEditing({ kind: it.kind, data: it.data })}
                    className="w-full text-left px-5 py-3 flex items-start gap-3 hover:bg-slate-50 border-t border-slate-100"
                    data-testid={`schedule-agenda-row-${it.data.id}`}
                  >
                    <div className={`w-1 self-stretch rounded ${COLOR_PILL[it.data.color] || (it.kind === "todo" ? (it.data.priority === "high" ? COLOR_PILL.rose : COLOR_PILL.amber) : COLOR_PILL.slate)}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900">{it.data.title}</div>
                      <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtTime(it.at)}</span>
                        {it.data.location && <span className="inline-flex items-center gap-1 truncate"><MapPin className="w-3 h-3" /> {it.data.location}</span>}
                        {it.kind === "todo" && (
                          <span className={`px-1.5 py-0.5 rounded border ${PRIORITY_PILL[it.data.priority] || PRIORITY_PILL.medium}`}>{it.data.priority}</span>
                        )}
                        {it.kind === "todo" && it.data.status === "done" && (
                          <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">done</span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}

        {view === "todos" && (
          <div className="space-y-4" data-testid="schedule-todos">
            <div className="bg-white border border-slate-200 rounded-md px-3 py-2 inline-flex items-center gap-1">
              <Filter className="w-3.5 h-3.5 text-slate-500 mr-1" />
              {[
                { id: "open", label: "Open" },
                { id: "high", label: "High priority" },
                { id: "done", label: "Done" },
                { id: "all", label: "All" },
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => setTodoFilter(f.id)}
                  data-testid={`schedule-todo-filter-${f.id}`}
                  className={`px-2.5 py-1 text-xs rounded ${
                    todoFilter === f.id ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {Object.entries(todoGroups).map(([label, items]) => {
              if (!items.length) return null;
              return (
                <div key={label} className="bg-white border border-slate-200 rounded-lg">
                  <div className={`px-4 py-2.5 text-xs uppercase tracking-widest font-bold border-b border-slate-100 flex items-center gap-2 ${
                    label === "Overdue" ? "text-rose-700 bg-rose-50/40" : "text-slate-600 bg-slate-50/40"
                  }`}>
                    {label === "Overdue" && <AlertTriangle className="w-3.5 h-3.5" />}
                    {label}
                    <span className="ml-1 text-slate-500 font-semibold">{items.length}</span>
                  </div>
                  {items.map((t) => (
                    <div
                      key={t.id}
                      className="px-4 py-3 border-b border-slate-100 last:border-b-0 flex items-start gap-3 hover:bg-slate-50/60"
                      data-testid={`schedule-todo-row-${t.id}`}
                    >
                      <button
                        onClick={() => (t.status === "open" ? completeTodo(t.id) : reopenTodo(t.id))}
                        data-testid={`schedule-todo-toggle-${t.id}`}
                        className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
                          t.status === "done"
                            ? "bg-emerald-600 border-emerald-600 text-white"
                            : "border-slate-300 hover:border-emerald-500"
                        }`}
                        title={t.status === "open" ? "Mark done" : "Reopen"}
                      >
                        {t.status === "done" && <CheckSquare className="w-3 h-3" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className={`font-semibold ${t.status === "done" ? "text-slate-400 line-through" : "text-slate-900"}`}>{t.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                          {t.due_at && (
                            <span className={`inline-flex items-center gap-1 ${t.status === "open" && isOverdue(t.due_at) ? "text-rose-700 font-semibold" : ""}`}>
                              <Clock className="w-3 h-3" /> {fmtDate(t.due_at)} {fmtTime(t.due_at)}
                            </span>
                          )}
                          <span className={`px-1.5 py-0.5 rounded border ${PRIORITY_PILL[t.priority] || PRIORITY_PILL.medium}`}>{t.priority}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                        <button
                          onClick={() => setEditing({ kind: "todo", data: t })}
                          data-testid={`schedule-todo-edit-${t.id}`}
                          className="p-1.5 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => deleteTodo(t.id)}
                          data-testid={`schedule-todo-delete-${t.id}`}
                          className="p-1.5 text-slate-400 hover:text-rose-700 hover:bg-rose-50 rounded"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
            {Object.values(todoGroups).every((g) => g.length === 0) && (
              <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-sm text-slate-500" data-testid="schedule-todos-empty">
                No todos in this filter. Click <span className="font-semibold">+ Todo</span> to add one.
              </div>
            )}
          </div>
        )}
      </div>

      {editing && (
        <EditorDialog
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            loadCalendar();
          }}
          onDeleteAppt={deleteAppt}
          onDeleteTodo={deleteTodo}
        />
      )}
    </DashboardShell>
  );
}

// ---------------- editor dialog ----------------

function EditorDialog({ editing, onClose, onSaved, onDeleteAppt, onDeleteTodo }) {
  const isAppt = editing.kind === "appointment";
  const existing = editing.data || null;
  const isNew = !existing;
  const [form, setForm] = useState(() => {
    if (isAppt) {
      return {
        title: existing?.title || "",
        description: existing?.description || "",
        start_at: existing ? toLocalInput(existing.start_at) : toLocalInput(new Date(Date.now() + 60 * 60 * 1000).toISOString()),
        end_at: existing ? toLocalInput(existing.end_at) : "",
        location: existing?.location || "",
        attendee_emails: (existing?.attendee_emails || []).join(", "),
        reminder_minutes_before: existing?.reminder_minutes_before ?? 30,
        color: existing?.color || "slate",
      };
    }
    return {
      title: existing?.title || "",
      description: existing?.description || "",
      due_at: existing ? toLocalInput(existing.due_at) : "",
      reminder_at: existing ? toLocalInput(existing.reminder_at) : "",
      priority: existing?.priority || "medium",
    };
  });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    setSaving(true);
    try {
      if (isAppt) {
        const payload = {
          title: form.title.trim(),
          description: form.description.trim() || null,
          start_at: fromLocalInput(form.start_at),
          end_at: form.end_at ? fromLocalInput(form.end_at) : null,
          location: form.location.trim() || null,
          attendee_emails: form.attendee_emails
            .split(/[,;\n]+/)
            .map((s) => s.trim())
            .filter(Boolean),
          reminder_minutes_before: Number(form.reminder_minutes_before) || 0,
          color: form.color,
        };
        if (!payload.start_at) {
          toast.error("Start time is required.");
          setSaving(false);
          return;
        }
        if (existing) await api.put(`/appointments/${existing.id}`, payload);
        else await api.post("/appointments", payload);
      } else {
        const payload = {
          title: form.title.trim(),
          description: form.description.trim() || null,
          due_at: form.due_at ? fromLocalInput(form.due_at) : null,
          reminder_at: form.reminder_at ? fromLocalInput(form.reminder_at) : null,
          priority: form.priority,
        };
        if (existing) await api.put(`/todos/${existing.id}`, payload);
        else await api.post("/todos", payload);
      }
      toast.success(isAppt ? "Appointment saved" : "Todo saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/50 flex items-center justify-center p-4" data-testid="schedule-editor">
      <div className="bg-white rounded-lg w-full max-w-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isAppt ? <Calendar className="w-4 h-4 text-emerald-700" /> : <CheckSquare className="w-4 h-4 text-slate-700" />}
            <h2 className="font-bold text-slate-900">
              {isNew ? `New ${editing.kind}` : `Edit ${editing.kind}`}
            </h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="schedule-editor-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <input
            type="text"
            placeholder="Title"
            value={form.title}
            onChange={(e) => update("title", e.target.value)}
            data-testid="schedule-editor-title"
            className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
            autoFocus
          />
          <textarea
            placeholder="Notes (optional)"
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 resize-none"
          />
          {isAppt ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Start</span>
                  <input
                    type="datetime-local"
                    value={form.start_at}
                    onChange={(e) => update("start_at", e.target.value)}
                    data-testid="schedule-editor-start"
                    className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 mt-1"
                  />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-widest font-bold text-slate-500">End (opt)</span>
                  <input
                    type="datetime-local"
                    value={form.end_at}
                    onChange={(e) => update("end_at", e.target.value)}
                    data-testid="schedule-editor-end"
                    className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 mt-1"
                  />
                </label>
              </div>
              <AddressAutocomplete
                value={form.location}
                onChange={(v) => update("location", v)}
                onVerified={({ formatted_address }) => update("location", formatted_address)}
                placeholder="Location — start typing, Google will suggest"
                className="input-clean w-full"
                dataTestId="schedule-editor-location"
              />
              <input
                type="text"
                placeholder="Attendees (emails, comma-separated)"
                value={form.attendee_emails}
                onChange={(e) => update("attendee_emails", e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Reminder</span>
                  <select
                    value={form.reminder_minutes_before}
                    onChange={(e) => update("reminder_minutes_before", e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 mt-1"
                  >
                    <option value="0">No reminder</option>
                    <option value="10">10 min before</option>
                    <option value="30">30 min before</option>
                    <option value="60">1 hour before</option>
                    <option value="180">3 hours before</option>
                    <option value="1440">1 day before</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Color</span>
                  <select
                    value={form.color}
                    onChange={(e) => update("color", e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 mt-1"
                  >
                    {Object.keys(COLOR_PILL).map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </label>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Due</span>
                  <input
                    type="datetime-local"
                    value={form.due_at}
                    onChange={(e) => update("due_at", e.target.value)}
                    data-testid="schedule-editor-due"
                    className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 mt-1"
                  />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Reminder at</span>
                  <input
                    type="datetime-local"
                    value={form.reminder_at}
                    onChange={(e) => update("reminder_at", e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 mt-1"
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Priority</span>
                <select
                  value={form.priority}
                  onChange={(e) => update("priority", e.target.value)}
                  data-testid="schedule-editor-priority"
                  className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 mt-1"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </label>
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div>
            {existing && (
              <button
                onClick={() => {
                  if (isAppt) onDeleteAppt(existing.id);
                  else onDeleteTodo(existing.id);
                  onClose();
                }}
                data-testid="schedule-editor-delete"
                className="text-sm text-rose-700 hover:text-rose-900 inline-flex items-center gap-1"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded">
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="schedule-editor-save"
              className="px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 rounded"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
